import * as React from "react";
import { connect } from "react-redux";
import { Grid } from "semantic-ui-react";
import { Authorize } from "reauthorize";

import { canUserAccessProductType } from "@teamprevent/common/productRestriction";
import { CaseType } from "@teamprevent/common/model";
import { DashboardIcon } from "@teamprevent/common/dashboard";
import { canManagerSeePhysiotherapyReferral } from "@teamprevent/common/productRestriction/canUserAccessPhysioOriginType";
import { useDispatch } from "@teamprevent/customer-redux";

import { ApplicationState } from "../../applicationState";
import { CUSTOMER_ROLES, anySraRole } from "../../auth/model";
import { AllowedAssessmentAndWellBeingFeatures } from "../../assessmentAndWellBeing/models";
import { CurrentUser } from "../../auth";

import { CustomerDetailsModel } from "../../customerDetails/models";
import { loadOhCaseCount, loadWorkAdjustmentCount } from "../actions";
import { loadActionableTasks } from "../../actionableTask/actions";

import { Users } from "./Users";
import Cases from "./Cases";
import Actions from "./Actions";
import { CreateCase } from "./CreateCase";

interface Props {
    allowedProducts: string[];
    allowedPhysioOrigins: string[];
    allowedAssessmentAndWellBeingFeatures: string[];
    currentUser: CurrentUser;
    customerDetails: CustomerDetailsModel;
}

export const DashboardUnconnected: React.FC<Props> = (props: Props) => {
    const dispatch = useDispatch();

    React.useEffect(() => {
        dispatch(loadOhCaseCount());
        dispatch(loadWorkAdjustmentCount());
        dispatch(loadActionableTasks());
    }, [dispatch]);

    if (props.customerDetails === null) {
        return null;
    }

    return (
        <Authorize authorize>
            <div className="dashboard">
                <Grid columns="4" doubling stackable centered>
                    {props.allowedProducts?.some(p => p !== CaseType.SRA) &&
                    <>
                        <Authorize authorize={CUSTOMER_ROLES.manager.name}>
                            {canUserAccessProductType(props.allowedProducts, CaseType.REFERRAL) &&
                                props.currentUser.ohDirect === false &&
                                <Grid.Column>
                                    <CreateCase href="/referral" text="Create New Referral" />
                                </Grid.Column>}
                        </Authorize>
                        {canUserAccessProductType(props.allowedProducts, CaseType.PPQ) && props.currentUser.ohDirect === false &&
                            <Authorize authorize={CUSTOMER_ROLES.recruitmentManager.name}>
                                <Grid.Column>
                                    <CreateCase href="/ppq" text="Create New PPQ" iconSource="/assets/create-ppq-case.svg" />
                                </Grid.Column>
                            </Authorize>}
                        {canUserAccessProductType(props.allowedProducts, CaseType.PHYSIOTHERAPY_REFERRAL) &&
                            canManagerSeePhysiotherapyReferral(props.allowedPhysioOrigins) &&
                            props.currentUser.ohDirect === false &&
                            <Authorize authorize={CUSTOMER_ROLES.manager.name}>
                                <Grid.Column>
                                    <CreateCase href="/physiotherapy" text="Create New Physiotherapy Referral" />
                                </Grid.Column>
                            </Authorize>}
                        <Grid.Column>
                            <Actions />
                        </Grid.Column>
                        <Grid.Column>
                            <Cases label="My Cases" include="All" />
                        </Grid.Column>
                        {canUserAccessProductType(props.allowedProducts, CaseType.GENERIC) &&
                            props.customerDetails.genericCaseTypes?.length > 0 &&
                            <Authorize authorize={CUSTOMER_ROLES.otherServices.name}>
                                <Grid.Column>
                                    <CreateCase href="/otherServices" text="Request Other Services" iconSource="/assets/request_other_services.svg" />
                                </Grid.Column>
                            </Authorize>}
                        {canUserAccessProductType(props.allowedProducts, CaseType.WORK_ADJUSTMENT) &&
                            <Grid.Column>
                                <Cases label="My Work Adjustments" include={CaseType.WORK_ADJUSTMENT} iconSource="/assets/my-work-adjustments.svg" />
                            </Grid.Column>}
                        <Authorize authorize={CUSTOMER_ROLES.customerAdministrator.name}>
                            <Grid.Column>
                                <Users />
                            </Grid.Column>
                        </Authorize>
                        <Grid.Column>
                            <DashboardIcon href="/statistics" icon="/assets/statistics.svg" label="Statistics" />
                        </Grid.Column>
                        <Grid.Column>
                            <DashboardIcon href="/documents" icon="/assets/create-ppq-case.svg" label="Documents & Links" />
                        </Grid.Column>
                    </>
                    }
                    {anySraRole(props.currentUser) &&
                        props.allowedAssessmentAndWellBeingFeatures?.indexOf(AllowedAssessmentAndWellBeingFeatures.SRA) > -1 &&
                        <Grid.Column>
                            <DashboardIcon href="/assessmentandwellbeing" icon="/assets/stress-risk-assessment.svg" label="Manage Stress Risk Assessments" />
                        </Grid.Column>
                    }
                </Grid>
            </div>
        </Authorize>
    );
};

const mapStateToProps = (state: ApplicationState): Props => ({
    allowedProducts: state.allowedProducts,
    allowedPhysioOrigins: state.allowedPhysioOrigins,
    allowedAssessmentAndWellBeingFeatures: state.allowedAssessmentAndWellBeingFeatures,
    currentUser: state.currentUser,
    customerDetails: state.customerDetails
});

export const Dashboard = connect(mapStateToProps)(DashboardUnconnected);
